import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dna, Search as SearchIcon, Brain, FlaskConical, Notebook, BookOpenText, Loader2, Plus, ChevronRight, MessageSquare, ArrowRight, Flame } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ReadAlongside from "../components/ReadAlongside";
import VoiceButton from "../components/VoiceButton";

const TABS = [
  { id: "chromosomes", label: "Chromosomes", icon: <Dna className="w-3 h-3" /> },
  { id: "gene",        label: "Gene detail", icon: <FlaskConical className="w-3 h-3" /> },
  { id: "simulate",    label: "Simulator",   icon: <Sparkles /> },
  { id: "ai",          label: "Mendel AI",   icon: <Brain className="w-3 h-3" /> },
  { id: "notebook",    label: "Notebook",    icon: <Notebook className="w-3 h-3" /> },
];

function Sparkles(){ return <span className="text-marigold">✦</span>; }

export default function MendelIDE() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const projectId = sp.get("project");
  const initTab = sp.get("tab") || "chromosomes";

  const [tab, setTab] = useState(initTab);
  const [project, setProject] = useState(null);
  const [chroms, setChroms] = useState([]);
  const [activeChrom, setActiveChrom] = useState(sp.get("chrom") || "11");
  const [activeGene, setActiveGene] = useState(sp.get("gene") || "HBB");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/mendel/chromosomes").then(({ data }) => setChroms(data.chromosomes || []));
    if (projectId) api.get(`/mendel/projects/${projectId}`).then(({ data }) => setProject(data)).catch(() => setProject(null));
    else setProject(null);
  }, [user, loading, projectId, nav]);

  const setTabPersist = (id) => { setTab(id); setSp({ ...Object.fromEntries(sp), tab: id }, { replace: true }); };

  if (loading) return <div className="pt-32 text-center text-ink2"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>;
  if (!user) return null;

  const runSearch = async (val) => {
    setQ(val);
    if (val.trim().length < 1) { setResults(null); return; }
    try {
      const { data } = await api.get(`/mendel/search?q=${encodeURIComponent(val.trim())}`);
      setResults(data);
    } catch { setResults(null); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-paper text-ink" data-testid="mendel-ide">
      <div className="grid grid-cols-12 gap-0">
        {/* Left sidebar */}
        <aside className="col-span-3 lg:col-span-2 border-r-2 border-ink bg-cream min-h-[calc(100vh-4rem)] p-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mb-2">Project</div>
          <div className="mb-3 text-sm font-semibold truncate">{project?.title || "Untitled workspace"}</div>

          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mb-1">Search</div>
            <div className="relative">
              <SearchIcon className="w-3.5 h-3.5 absolute left-2 top-2 text-ink2" />
              <input
                value={q}
                onChange={(e) => runSearch(e.target.value)}
                placeholder="Gene, chromosome…"
                className="w-full pl-7 pr-2 py-1.5 rounded-md border-2 border-ink bg-cream text-xs"
                data-testid="mendel-ide-search"
              />
            </div>
            {results && (
              <div className="mt-2 text-xs" data-testid="mendel-ide-search-results">
                {results.genes.length > 0 && (
                  <ul className="mb-2">{results.genes.map((g) => (
                    <li key={g.id}><button onClick={() => { setActiveGene(g.symbol); setTabPersist("gene"); }} className="text-marigold hover:underline">{g.symbol}</button> <span className="text-ink2">· {g.name}</span></li>
                  ))}</ul>
                )}
                {results.chromosomes.length > 0 && (
                  <ul className="mb-2">{results.chromosomes.map((c) => (
                    <li key={c.id}><button onClick={() => { setActiveChrom(c.name); setTabPersist("chromosomes"); }} className="text-marigold hover:underline">Chr {c.name}</button></li>
                  ))}</ul>
                )}
                {results.genes.length === 0 && results.chromosomes.length === 0 && <div className="text-ink2">No matches</div>}
              </div>
            )}
          </div>

          <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mb-2">Explorer</div>
          <ul className="space-y-1 text-sm">
            <li><SidebarLink icon="🧬" label="Chromosomes" onClick={() => setTabPersist("chromosomes")} active={tab === "chromosomes"} /></li>
            <li><SidebarLink icon="🔬" label="Gene detail" onClick={() => setTabPersist("gene")} active={tab === "gene"} /></li>
            <li><SidebarLink icon="✦"  label="Simulator" onClick={() => setTabPersist("simulate")} active={tab === "simulate"} /></li>
            <li><SidebarLink icon="🧠" label="Mendel AI" onClick={() => setTabPersist("ai")} active={tab === "ai"} /></li>
            {project && <li><SidebarLink icon="📓" label="Notebook" onClick={() => setTabPersist("notebook")} active={tab === "notebook"} /></li>}
          </ul>

          <div className="mt-6 pt-4 border-t border-ink2/20">
            <Link to="/mendel" className="text-xs text-ink2 hover:text-ink inline-flex items-center gap-1"><ChevronRight className="w-3 h-3 rotate-180" /> Back to dashboard</Link>
          </div>
        </aside>

        {/* Main workspace */}
        <main className="col-span-9 lg:col-span-10 p-6">
          {/* Tab bar */}
          <div className="flex items-center gap-1 mb-6 border-b-2 border-ink" data-testid="mendel-ide-tabs">
            {TABS.filter(t => t.id !== "notebook" || project).map((t) => (
              <button
                key={t.id}
                onClick={() => setTabPersist(t.id)}
                data-testid={`mendel-ide-tab-${t.id}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1.5 border-b-2 -mb-0.5 ${tab === t.id ? "border-marigold text-marigold" : "border-transparent text-ink2 hover:text-ink"}`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* Read alongside — chapters from the curriculum that pair with what's in view */}
          <div className="mb-6">
            <ReadAlongside
              seeds={tab === "chromosomes" ? `data structures loops sequences chromosome ${activeChrom}` :
                     tab === "gene" ? `strings text parsing symbols encoding gene ${activeGene}` :
                     tab === "simulate" ? `probability randomness state simulation punnett` :
                     tab === "ai" ? `functions abstraction interfaces AI prompt` :
                     `variables data structures loops ${activeChrom} ${activeGene}`}
              eyebrow="Read alongside as you explore"
              title="chapters from the curriculum for this workbench"
              askPrompt={`I'm in the ${tab} tab of Mendel with chromosome ${activeChrom} and gene ${activeGene}. What curriculum ideas will help me?`}
              variant="ribbon"
              defaultOpen={false}
              testId="mendel-read-alongside"
            />
          </div>

          {tab === "chromosomes" && (
            <div className="grid lg:grid-cols-[1fr_280px] gap-6">
              <ChromosomesPane chroms={chroms} activeChrom={activeChrom} setActiveChrom={setActiveChrom} onOpenGene={(sym) => { setActiveGene(sym); setTabPersist("gene"); }} />
              <StudyModePanel kind="chromosome" target={activeChrom} />
            </div>
          )}
          {tab === "gene" && (
            <div className="grid lg:grid-cols-[1fr_280px] gap-6">
              <GenePane symbol={activeGene} setActiveGene={setActiveGene} project={project} onRefreshProject={() => projectId && api.get(`/mendel/projects/${projectId}`).then(({ data }) => setProject(data))} />
              <StudyModePanel kind="gene" target={activeGene} />
            </div>
          )}
          {tab === "simulate" && <SimulatePane geneSymbol={activeGene} setActiveGene={setActiveGene} project={project} onRefreshProject={() => projectId && api.get(`/mendel/projects/${projectId}`).then(({ data }) => setProject(data))} />}
          {tab === "ai" && <AIPane context={{ gene: activeGene, chromosome: activeChrom, project_id: projectId }} project={project} onRefreshProject={() => projectId && api.get(`/mendel/projects/${projectId}`).then(({ data }) => setProject(data))} />}
          {tab === "notebook" && project && <NotebookPane project={project} onRefreshProject={() => api.get(`/mendel/projects/${projectId}`).then(({ data }) => setProject(data))} />}
        </main>
      </div>
    </div>
  );
}

function SidebarLink({ icon, label, onClick, active }) {
  return (
    <button onClick={onClick} className={`w-full text-left px-2 py-1 rounded flex items-center gap-2 text-sm ${active ? "bg-marigold/20 text-marigold font-semibold" : "hover:bg-ink/5"}`}>
      <span className="w-4 text-center">{icon}</span> {label}
    </button>
  );
}

// ─────────── Chromosomes pane ───────────
function ChromosomesPane({ chroms, activeChrom, setActiveChrom, onOpenGene }) {
  const [detail, setDetail] = useState(null);
  const [zoomChrom, setZoomChrom] = useState(null);
  useEffect(() => {
    if (!activeChrom) return;
    api.get(`/mendel/chromosomes/${activeChrom}`).then(({ data }) => setDetail(data)).catch(() => setDetail(null));
  }, [activeChrom]);

  return (
    <div data-testid="mendel-chromosomes-pane">
      <h2 className="font-heading text-2xl mb-1">Human karyogram</h2>
      <p className="text-ink2 text-sm mb-6">GRCh38 · 24 chromosomes · click any to inspect, double-click to open the gene-density heatmap.</p>

      {/* Karyogram — vertical bars scaled by length */}
      <div className="grid grid-cols-12 gap-2 mb-8" data-testid="karyogram">
        {chroms.map((c) => {
          const h = Math.max(48, (c.length_mb / 250) * 200);
          const centromereTop = (c.centromere_mb / c.length_mb) * 100;
          const isActive = c.name === activeChrom;
          return (
            <button key={c.id} onClick={() => setActiveChrom(c.name)} onDoubleClick={() => setZoomChrom(c.name)} className="flex flex-col items-center group" data-testid={`karyogram-${c.name}`} title="Double-click for the density heatmap">
              <div className={`relative w-full rounded-full border-2 transition ${isActive ? "border-marigold bg-marigold/20" : "border-ink bg-ink/10 group-hover:border-marigold"}`} style={{ height: `${h}px` }}>
                <div className="absolute left-0 right-0 border-t border-dashed border-ink2/60" style={{ top: `${centromereTop}%` }} />
              </div>
              <div className={`text-xs font-mono mt-1 ${isActive ? "text-marigold font-bold" : "text-ink2"}`}>{c.name}</div>
            </button>
          );
        })}
      </div>

      {detail && (
        <div className="card-soft p-5" data-testid={`chromosome-detail-${detail.chromosome.name}`}>
          <div className="flex items-baseline justify-between mb-1">
            <h3 className="font-heading text-2xl">Chromosome {detail.chromosome.name}</h3>
            <div className="flex items-center gap-3">
              <button onClick={() => setZoomChrom(detail.chromosome.name)} className="btn-outline-ink text-[10px] uppercase tracking-widest font-bold px-3 py-1" data-testid={`karyogram-zoom-open-${detail.chromosome.name}`}>
                🔍 Zoom &amp; density
              </button>
              <div className="text-xs font-mono text-ink2">{detail.chromosome.length_mb} Mb · centromere ≈ {detail.chromosome.centromere_mb} Mb</div>
            </div>
          </div>
          <div className="text-sm text-ink2 mb-4">{detail.chromosome.kind === "sex" ? "Sex chromosome" : "Autosome"} · {detail.genes.length} featured gene{detail.genes.length !== 1 ? "s" : ""}</div>

          {detail.genes.length > 0 ? (
            <ul className="divide-y divide-ink2/10" data-testid="chromosome-gene-list">
              {detail.genes.map((g) => (
                <li key={g.id} className="py-2 flex items-center justify-between gap-3">
                  <div>
                    <button onClick={() => onOpenGene(g.symbol)} className="font-hand text-xl text-marigold hover:underline">{g.symbol}</button>
                    <span className="ml-2 text-sm text-ink">{g.name}</span>
                  </div>
                  <div className="text-[10px] font-mono text-ink2">{(g.start / 1e6).toFixed(2)}–{(g.end / 1e6).toFixed(2)} Mb · {g.strand}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-ink2 text-sm">No featured genes yet on this chromosome — try 11, 16, or 17.</div>
          )}
        </div>
      )}

      {zoomChrom && (
        <KaryogramZoomModal
          chromName={zoomChrom}
          onClose={() => setZoomChrom(null)}
          onOpenGene={(sym) => { onOpenGene(sym); setZoomChrom(null); }}
        />
      )}
    </div>
  );
}

// ─────────── Karyogram Zoom (gene-density heatmap) ───────────
function KaryogramZoomModal({ chromName, onClose, onOpenGene }) {
  const [data, setData] = useState(null);
  const [zoom, setZoom] = useState(1);      // 1x – 4x
  const [panMb, setPanMb] = useState(0);    // Mb offset from left when zoomed
  const [hover, setHover] = useState(null); // {bucket} or null

  useEffect(() => {
    if (!chromName) return;
    setData(null); setZoom(1); setPanMb(0); setHover(null);
    api.get(`/mendel/chromosomes/${chromName}/density?bins=20`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [chromName]);

  if (!chromName) return null;

  const lengthMb = data?.chromosome?.length_mb || 100;
  const centromereMb = data?.chromosome?.centromere_mb || null;

  // Visible window (Mb) based on zoom + pan
  const visibleMb = lengthMb / zoom;
  const viewStart = Math.max(0, Math.min(panMb, lengthMb - visibleMb));
  const viewEnd = viewStart + visibleMb;

  // SVG geometry
  const W = 720, H = 96;
  const pad = 20;
  const trackW = W - pad * 2;
  const pxPerMb = trackW / visibleMb;
  const mbToX = (mb) => pad + (mb - viewStart) * pxPerMb;

  // Heatmap colours — dark marigold for high density.
  const colourFor = (count, max) => {
    if (!count) return "#EEE7D6";
    const t = max > 0 ? count / max : 0;
    // interpolate cream → marigold (#F5A623)
    const r = Math.round(238 + (245 - 238) * t);
    const g = Math.round(231 + (166 - 231) * t);
    const b = Math.round(214 + (35 - 214) * t);
    return `rgb(${r},${g},${b})`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose} data-testid="karyogram-zoom-modal">
      <div className="bg-cream border-2 border-ink rounded-3xl shadow-[10px_10px_0_0_#1A1B41] w-full max-w-3xl p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-4 text-ink2 text-2xl w-8 h-8" aria-label="Close" data-testid="karyogram-zoom-close">×</button>

        <div className="label-eyebrow mb-1">Gene-density heatmap</div>
        <h3 className="font-heading text-3xl mb-1">Chromosome {chromName}</h3>
        <p className="text-ink2 text-sm mb-4">{lengthMb} Mb · pan and zoom to inspect featured genes across the arm.</p>

        {!data ? (
          <div className="text-ink2 text-sm py-10 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-widest font-bold text-ink2">Zoom</div>
              <div className="inline-flex gap-1" data-testid="karyogram-zoom-controls">
                {[1, 2, 3, 4].map((z) => (
                  <button key={z} onClick={() => { setZoom(z); setPanMb((prev) => Math.max(0, Math.min(prev, lengthMb - lengthMb / z))); }} className={`px-2.5 py-1 text-xs rounded border-2 ${zoom === z ? "border-marigold bg-marigold/20 text-marigold font-bold" : "border-ink2/40 text-ink2 hover:border-ink"}`} data-testid={`karyogram-zoom-${z}x`}>{z}x</button>
                ))}
              </div>
              {zoom > 1 && (
                <div className="flex items-center gap-2 ml-2">
                  <button onClick={() => setPanMb((p) => Math.max(0, p - visibleMb / 4))} className="px-2 py-1 border-2 border-ink2/40 rounded text-xs" data-testid="karyogram-pan-left">←</button>
                  <div className="text-[10px] font-mono text-ink2" data-testid="karyogram-visible-range">{viewStart.toFixed(1)} – {viewEnd.toFixed(1)} Mb</div>
                  <button onClick={() => setPanMb((p) => Math.min(lengthMb - visibleMb, p + visibleMb / 4))} className="px-2 py-1 border-2 border-ink2/40 rounded text-xs" data-testid="karyogram-pan-right">→</button>
                </div>
              )}
            </div>

            <div className="rounded-xl border-2 border-ink bg-paper p-3" data-testid="karyogram-heatmap">
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={`Gene-density heatmap for chromosome ${chromName}`}>
                {/* Axis line */}
                <line x1={pad} y1={H - 22} x2={W - pad} y2={H - 22} stroke="#1A1B41" strokeWidth="1" />
                {/* Ticks — 5 evenly spaced Mb labels across the visible window */}
                {[0, 1, 2, 3, 4].map((i) => {
                  const mb = viewStart + (visibleMb * i) / 4;
                  const x = mbToX(mb);
                  return (
                    <g key={`tk-${i}`}>
                      <line x1={x} y1={H - 22} x2={x} y2={H - 18} stroke="#1A1B41" />
                      <text x={x} y={H - 6} textAnchor="middle" fontSize="9" fill="#7A7A7A" className="font-mono">{mb.toFixed(1)} Mb</text>
                    </g>
                  );
                })}
                {/* Centromere marker */}
                {centromereMb !== null && centromereMb >= viewStart && centromereMb <= viewEnd && (
                  <g data-testid="karyogram-centromere">
                    <line x1={mbToX(centromereMb)} y1={12} x2={mbToX(centromereMb)} y2={H - 22} stroke="#C1666B" strokeDasharray="3 3" strokeWidth="1.5" />
                    <text x={mbToX(centromereMb)} y={9} textAnchor="middle" fontSize="8" fill="#C1666B">centromere</text>
                  </g>
                )}
                {/* Buckets (heatmap cells) — only render those overlapping the visible window */}
                {data.buckets.filter((b) => b.end_mb >= viewStart && b.start_mb <= viewEnd).map((b) => {
                  const x = mbToX(Math.max(b.start_mb, viewStart));
                  const x2 = mbToX(Math.min(b.end_mb, viewEnd));
                  const w = Math.max(1, x2 - x);
                  return (
                    <g key={`b-${b.index}`}>
                      <rect
                        x={x} y={26} width={w} height={40}
                        fill={colourFor(b.gene_count, data.max_count)}
                        stroke="#1A1B41" strokeWidth="0.5"
                        data-testid={`karyogram-bucket-${b.index}`}
                        onMouseEnter={() => setHover(b)}
                        onMouseLeave={() => setHover(null)}
                        style={{ cursor: b.genes.length > 0 ? "pointer" : "default" }}
                        onClick={() => { if (b.genes.length > 0) onOpenGene(b.genes[0].symbol); }}
                      >
                        <title>{`${b.start_mb}-${b.end_mb} Mb · ${b.gene_count} featured gene${b.gene_count !== 1 ? "s" : ""}`}</title>
                      </rect>
                      {b.gene_count > 0 && w > 12 && (
                        <text x={(x + x2) / 2} y={50} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#1A1B41" pointerEvents="none">{b.gene_count}</text>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>

            {/* Legend + hover detail */}
            <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-[10px] font-mono text-ink2" data-testid="karyogram-legend">
                <span>Low</span>
                <div className="flex">
                  {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                    <div key={t} className="w-6 h-3 border border-ink" style={{ background: colourFor(Math.round(t * (data.max_count || 1)), data.max_count || 1) }} />
                  ))}
                </div>
                <span>High · max {data.max_count} gene{data.max_count !== 1 ? "s" : ""}/window</span>
              </div>
              {hover && (
                <div className="text-xs text-ink2" data-testid="karyogram-hover-detail">
                  <span className="font-mono text-marigold">{hover.start_mb.toFixed(1)}–{hover.end_mb.toFixed(1)} Mb</span>
                  {hover.genes.length > 0 ? (
                    <span className="ml-2">· {hover.genes.map((g) => (
                      <button key={g.symbol} onClick={() => onOpenGene(g.symbol)} className="text-marigold hover:underline font-hand text-lg mr-1">{g.symbol}</button>
                    ))}</span>
                  ) : (
                    <span className="ml-2 italic">no featured genes yet</span>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────── Gene body diagram ───────────
function GeneBodySVG({ gene, variants }) {
  if (!gene?.exons?.length) return null;
  const geneLen = Math.max(1, gene.end - gene.start);
  // The exons dictionary uses coords relative to gene start.
  const width = 620, height = 78;
  const track = { x: 20, y: 40, w: width - 40, h: 8 };
  const posX = (bp) => track.x + (bp / geneLen) * track.w;
  // Estimate variant offsets from HGVS c.NNN patterns (fallback: middle)
  const variantOffsets = (variants || []).map((v) => {
    const m = (v.hgvs || "").match(/c\.(\d+)/);
    const offset = m ? parseInt(m[1], 10) : geneLen / 2;
    return { ...v, bp: Math.max(0, Math.min(geneLen - 1, offset)) };
  });
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="Gene body diagram" data-testid="gene-body-svg">
      {/* Backbone (introns) */}
      <line x1={track.x} y1={track.y + track.h / 2} x2={track.x + track.w} y2={track.y + track.h / 2} stroke="#1A1B41" strokeWidth="1.5" />
      {/* Strand arrow */}
      <polygon points={`${track.x + track.w},${track.y + track.h / 2} ${track.x + track.w - 6},${track.y + track.h / 2 - 4} ${track.x + track.w - 6},${track.y + track.h / 2 + 4}`} fill="#1A1B41" />
      {/* Exons */}
      {gene.exons.map((ex) => {
        const x = posX(ex.start);
        const w = Math.max(2, posX(ex.end) - x);
        return (
          <g key={`ex-${ex.n}`} data-testid={`gene-exon-${ex.n}`}>
            <rect x={x} y={track.y - 3} width={w} height={track.h + 6} fill="#7B5EA7" stroke="#1A1B41" strokeWidth="1" rx="1.5" />
            <title>{`Exon ${ex.n} · ${ex.start}-${ex.end} bp`}</title>
          </g>
        );
      })}
      {/* Variant markers */}
      {variantOffsets.map((v, i) => {
        const x = posX(v.bp);
        return (
          <g key={`var-${i}`} data-testid={`gene-variant-marker-${i}`}>
            <line x1={x} y1={track.y - 14} x2={x} y2={track.y - 4} stroke="#C1666B" strokeWidth="2" />
            <circle cx={x} cy={track.y - 18} r="4" fill="#C1666B" stroke="#1A1B41" strokeWidth="1" />
            <title>{`${v.protein_change || v.hgvs} · ${v.phenotype || ""}`}</title>
          </g>
        );
      })}
      {/* Axis */}
      <text x={track.x} y={height - 8} className="font-mono" style={{ fontSize: 9 }} fill="#7A7A7A">0</text>
      <text x={track.x + track.w} y={height - 8} textAnchor="end" className="font-mono" style={{ fontSize: 9 }} fill="#7A7A7A">{(geneLen / 1000).toFixed(1)} kb</text>
      <text x={track.x + track.w / 2} y={height - 8} textAnchor="middle" className="font-mono" style={{ fontSize: 9 }} fill="#7A7A7A">
        {gene.symbol} · {gene.exons.length} exon{gene.exons.length !== 1 ? "s" : ""} · {gene.strand} strand
      </text>
    </svg>
  );
}

// ─────────── Study Mode quiz panel ───────────
function StudyModePanel({ kind, target }) {
  const [busy, setBusy] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [reveal, setReveal] = useState({});
  const [streak, setStreak] = useState(null);
  const [attemptRecorded, setAttemptRecorded] = useState(false);

  useEffect(() => {
    api.get("/quiz/streak/me").then(({ data }) => setStreak(data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!target) return;
    setBusy(true); setReveal({}); setAttemptRecorded(false);
    try {
      const { data } = await api.post("/mendel/quiz", { kind, target });
      setQuestions(data.questions || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate quiz"); }
    finally { setBusy(false); }
  };

  // Auto-record an attempt when the user reveals all three answers.
  useEffect(() => {
    if (attemptRecorded) return;
    if (questions.length < 3) return;
    const allRevealed = questions.every((_, i) => reveal[i]);
    if (!allRevealed) return;
    setAttemptRecorded(true);
    api.post("/quiz/attempt", { kind, target }).then(({ data }) => setStreak(data)).catch(() => {});
  }, [reveal, questions, attemptRecorded, kind, target]);

  return (
    <aside className="card-soft p-4 sticky top-6" data-testid="study-mode-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow inline-flex items-center gap-1.5"><span className="text-marigold">✎</span> Study mode</div>
        <button onClick={generate} disabled={busy || !target} className="btn-outline-ink text-[10px] uppercase tracking-widest font-bold px-3 py-1" data-testid="study-mode-generate">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : questions.length ? "Regenerate" : "Quiz me"}
        </button>
      </div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-[10px] font-mono text-ink2">{kind}: <span className="text-marigold">{target || "…"}</span></div>
        <StreakPill streak={streak} testid="study-mode-streak" />
      </div>
      {questions.length === 0 ? (
        <div className="text-ink2 text-xs">Tap <em>Quiz me</em> for 3 short questions about the current {kind}.  Answers are one tap away.</div>
      ) : (
        <>
          <VoicePlayAll questions={questions} testid="study-mode-play-all" />
          <ol className="space-y-3 mt-2">{questions.map((q, i) => (
            <li key={i} className="border-t border-ink2/15 pt-3" data-testid={`study-mode-q-${i}`}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <div className="text-[9px] uppercase tracking-widest font-bold text-marigold">{q.difficulty || "Q"}{i + 1}</div>
                <VoiceButton
                  text={`Question ${i + 1}. ${q.q}${reveal[i] ? `. Answer: ${q.a}` : ""}`}
                  label={reveal[i] ? "Listen (with answer)" : "Listen"}
                  className="!px-2 !py-0.5 !text-[9px]"
                  data-testid={`study-mode-voice-${i}`}
                />
              </div>
              <div className="text-sm mb-1">{q.q}</div>
              {reveal[i] ? (
                <div className="text-xs text-ink2 border-l-2 border-marigold pl-2 italic" data-testid={`study-mode-a-${i}`}>{q.a}</div>
              ) : (
                <button onClick={() => setReveal((r) => ({ ...r, [i]: true }))} className="text-[10px] text-marigold underline uppercase tracking-widest font-bold" data-testid={`study-mode-reveal-${i}`}>Reveal answer</button>
              )}
            </li>
          ))}</ol>
        </>
      )}
    </aside>
  );
}

// ─────────── Streak pill ───────────
export function StreakPill({ streak, testid = "streak-pill" }) {
  if (!streak) return null;
  const cur = Number(streak.current || 0);
  const best = Number(streak.best || 0);
  if (cur === 0 && best === 0) return null;
  return (
    <div
      className="inline-flex items-center gap-1 rounded-full border border-marigold/40 bg-marigold/10 px-2.5 py-0.5 text-[10px] font-mono text-marigold"
      title={streak.stale ? "Streak went cold — quiz today to restart it" : "Reveal all 3 answers to bump your streak"}
      data-testid={testid}
    >
      <Flame className={`w-3 h-3 ${cur > 0 ? "" : "opacity-50"}`} />
      <span className="font-bold">{cur}</span>
      <span className="opacity-70">· best {best}</span>
    </div>
  );
}

// ─────────── Voice Play-All ───────────
export function VoicePlayAll({ questions, testid = "voice-play-all" }) {
  const script = questions.map((q, idx) => `Question ${idx + 1}. ${q.q}. Answer: ${q.a}.`).join("  ");
  return (
    <div className="flex items-center justify-between gap-2 mb-2" data-testid={testid}>
      <div className="text-[10px] font-mono text-ink2">🎧 Hands-free revision</div>
      <VoiceButton
        text={script}
        label="Play all"
        className="!px-2 !py-0.5 !text-[9px]"
        data-testid={`${testid}-btn`}
      />
    </div>
  );
}

// ─────────── Gene pane ───────────
function GenePane({ symbol, setActiveGene, project, onRefreshProject }) {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState(symbol);
  useEffect(() => {
    if (!symbol) return;
    api.get(`/mendel/genes/${symbol}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [symbol]);

  const addToNotebook = async () => {
    if (!project) { toast.info("Open or create a project to save notes"); return; }
    try {
      await api.post(`/mendel/projects/${project.id}/notebook`, { kind: "gene", target: symbol, note: `Studied gene ${symbol}` });
      toast.success("Added to notebook");
      onRefreshProject?.();
    } catch { toast.error("Could not save"); }
  };

  return (
    <div data-testid="mendel-gene-pane">
      <div className="flex items-center gap-2 mb-4">
        <input value={pick} onChange={(e) => setPick(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") setActiveGene(pick); }} className="px-3 py-1.5 rounded-md border-2 border-ink bg-cream text-sm" placeholder="Gene symbol" data-testid="gene-input" />
        <button onClick={() => setActiveGene(pick)} className="btn-outline-ink text-xs" data-testid="gene-load">Load</button>
      </div>
      {!data ? (
        <div className="text-ink2 text-sm">No gene loaded. Try HBB, BRCA1, TP53, MC1R…</div>
      ) : (
        <div>
          <div className="flex items-baseline gap-3 mb-1">
            <h2 className="font-heading text-4xl">{data.gene.symbol}</h2>
            <span className="text-ink2 text-sm">{data.gene.name}</span>
          </div>
          <div className="text-xs font-mono text-ink2 mb-4">Chromosome {data.gene.chromosome} · {(data.gene.start / 1e6).toFixed(2)}–{(data.gene.end / 1e6).toFixed(2)} Mb · {data.gene.strand} strand · {data.gene.biotype}</div>
          <p className="text-ink leading-relaxed mb-4 max-w-2xl">{data.gene.summary}</p>

          {/* Gene body diagram */}
          {data.gene.exons?.length > 0 && (
            <div className="card-soft p-4 mb-4" data-testid="gene-body-diagram">
              <div className="label-eyebrow mb-2">Gene body · exons and introns to scale</div>
              <GeneBodySVG gene={data.gene} variants={data.variants} />
              <div className="flex items-center gap-4 text-[10px] font-mono text-ink2 mt-2">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#7B5EA7] border border-ink" /> Exon</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-ink" /> Intron</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#C1666B] border border-ink" /> Variant</span>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="card-soft p-4">
              <div className="label-eyebrow mb-1">Protein</div>
              <div className="font-hand text-2xl text-marigold">{data.gene.protein_name}</div>
              <div className="text-ink2 text-xs mt-1">{data.gene.aa_length} amino acids</div>
            </div>
            <div className="card-soft p-4">
              <div className="label-eyebrow mb-1">Inheritance</div>
              <div className="font-hand text-2xl text-marigold">{(data.gene.inheritance || "").replace("_", " ")}</div>
              {data.gene.phenotype_dominant && <div className="text-ink2 text-xs mt-1">Dominant: {data.gene.phenotype_dominant}</div>}
              {data.gene.phenotype_recessive && <div className="text-ink2 text-xs">Recessive: {data.gene.phenotype_recessive}</div>}
            </div>
          </div>

          {data.variants.length > 0 && (
            <div className="mb-4">
              <div className="label-eyebrow mb-2">Featured variants</div>
              <ul className="space-y-2" data-testid="gene-variants">
                {data.variants.map((v) => (
                  <li key={v.id} className="card-soft p-3">
                    <div className="font-mono text-xs text-marigold">{v.hgvs} · {v.protein_change}</div>
                    <div className="text-sm font-semibold mt-1">{v.phenotype}</div>
                    <div className="text-ink2 text-xs mt-1">{v.note}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button onClick={addToNotebook} className="btn-outline-ink text-xs" data-testid="gene-add-notebook"><Plus className="w-3 h-3" /> Add to notebook</button>
        </div>
      )}
    </div>
  );
}

// ─────────── Simulator pane ───────────
function SimulatePane({ geneSymbol, setActiveGene, project, onRefreshProject }) {
  const [gene, setGene] = useState(geneSymbol || "HBB");
  const [p1, setP1] = useState("Aa");
  const [p2, setP2] = useState("Aa");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mendel/simulate/mendelian", { gene_symbol: gene, parent1: p1, parent2: p2 });
      setResult(data);
      setActiveGene(gene);
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulation failed"); }
    finally { setBusy(false); }
  };

  const saveToNotebook = async () => {
    if (!project) { toast.info("Open a project first"); return; }
    if (!result) return;
    const note = `Punnett ${p1} × ${p2}: AA=${(result.genotypes.AA*100).toFixed(0)}% Aa=${(result.genotypes.Aa*100).toFixed(0)}% aa=${(result.genotypes.aa*100).toFixed(0)}%`;
    await api.post(`/mendel/projects/${project.id}/notebook`, { kind: "simulation", target: gene, note });
    toast.success("Saved simulation");
    onRefreshProject?.();
  };

  return (
    <div data-testid="mendel-simulate-pane">
      <h2 className="font-heading text-3xl mb-1">Mendelian inheritance simulator</h2>
      <p className="text-ink2 text-sm mb-6">Uppercase = dominant allele.  Lowercase = recessive.  Cross two parents to see genotype and phenotype probabilities.</p>

      <div className="card-soft p-5 mb-4 grid md:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold mb-1">Gene</div>
          <input value={gene} onChange={(e) => setGene(e.target.value.toUpperCase())} className="w-full px-2 py-1.5 rounded-md border-2 border-ink bg-cream text-sm" data-testid="sim-gene" />
        </div>
        {["Parent 1", "Parent 2"].map((label, i) => (
          <div key={label}>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1">{label}</div>
            <select value={i === 0 ? p1 : p2} onChange={(e) => (i === 0 ? setP1 : setP2)(e.target.value)} className="w-full px-2 py-1.5 rounded-md border-2 border-ink bg-cream text-sm" data-testid={`sim-p${i+1}`}>
              <option value="AA">AA · homozygous dominant</option>
              <option value="Aa">Aa · heterozygous carrier</option>
              <option value="aa">aa · homozygous recessive</option>
            </select>
          </div>
        ))}
        <div className="flex items-end">
          <button onClick={run} disabled={busy} className="btn-primary text-sm w-full" data-testid="sim-run">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run cross"}
          </button>
        </div>
      </div>

      {result && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="sim-result">
          <div className="card-soft p-5">
            <div className="label-eyebrow mb-3">Punnett square</div>
            <div className="grid grid-cols-3 gap-0 border-2 border-ink rounded-xl overflow-hidden max-w-xs">
              <div />
              <div className="text-center font-mono bg-ink/5 py-2">{result.cells[0].parent2_allele}</div>
              <div className="text-center font-mono bg-ink/5 py-2">{result.cells[1].parent2_allele}</div>
              <div className="text-center font-mono bg-ink/5 flex items-center justify-center">{result.cells[0].parent1_allele}</div>
              <div className="text-center font-hand text-2xl text-marigold py-3">{result.cells[0].genotype}</div>
              <div className="text-center font-hand text-2xl text-marigold py-3">{result.cells[1].genotype}</div>
              <div className="text-center font-mono bg-ink/5 flex items-center justify-center">{result.cells[2].parent1_allele}</div>
              <div className="text-center font-hand text-2xl text-marigold py-3">{result.cells[2].genotype}</div>
              <div className="text-center font-hand text-2xl text-marigold py-3">{result.cells[3].genotype}</div>
            </div>
          </div>
          <div className="card-soft p-5">
            <div className="label-eyebrow mb-3">Probabilities</div>
            <Row label="AA" pct={result.genotypes.AA} />
            <Row label="Aa" pct={result.genotypes.Aa} />
            <Row label="aa" pct={result.genotypes.aa} />
            <div className="border-t border-ink2/20 my-3" />
            <Row label="Dominant phenotype" pct={result.phenotypes.dominant} strong />
            <Row label="Recessive phenotype" pct={result.phenotypes.recessive} strong />
            <p className="text-[10px] text-ink2 mt-3 italic">{result.disclaimer}</p>
            <button onClick={saveToNotebook} className="btn-outline-ink text-xs mt-3" data-testid="sim-save"><Plus className="w-3 h-3" /> Save to notebook</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, pct, strong }) {
  return (
    <div className="flex items-center justify-between text-sm mb-1.5">
      <span className={strong ? "font-semibold" : ""}>{label}</span>
      <span className="font-mono text-marigold font-bold">{(pct * 100).toFixed(0)}%</span>
    </div>
  );
}

// ─────────── AI pane ───────────
function AIPane({ context, project, onRefreshProject }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!input.trim() || busy) return;
    const msg = input.trim();
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/mendel/ai/chat", { session_id: sessionId, message: msg, context });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ " + (e?.response?.data?.detail || "Mendel AI is temporarily unreachable.") }]);
    } finally { setBusy(false); }
  };

  const saveToNotebook = async (content) => {
    if (!project) { toast.info("Open a project first"); return; }
    await api.post(`/mendel/projects/${project.id}/notebook`, { kind: "ask_ai", target: context?.gene || "", note: content.slice(0, 1500) });
    toast.success("Saved to notebook");
    onRefreshProject?.();
  };

  const suggestions = [
    context?.gene ? `Explain ${context.gene} like I am a programmer.` : "Explain HBB like I am a programmer.",
    "What is genetic drift?",
    "Where is the uncertainty in a Punnett-square prediction?",
  ];

  return (
    <div data-testid="mendel-ai-pane" className="max-w-3xl mx-auto">
      <h2 className="font-heading text-3xl mb-1">Mendel AI</h2>
      <p className="text-ink2 text-sm mb-6">Powered by OpenAI GPT-5.4.  Aware of your active workspace {context?.gene && <span className="font-mono">· gene: {context.gene}</span>} {context?.chromosome && <span className="font-mono">· chr {context.chromosome}</span>}.</p>

      <div className="card-soft p-5 mb-3 min-h-[280px] max-h-[420px] overflow-y-auto space-y-3" data-testid="mendel-ai-transcript">
        {messages.length === 0 ? (
          <div className="text-ink2 text-sm">
            <p className="mb-3">Ask me anything about your active workspace.  I'll cite where I'm confident and flag where I'm not.</p>
            <div className="flex flex-wrap gap-2">{suggestions.map((s, i) => (
              <button key={i} onClick={() => setInput(s)} className="text-xs rounded-full border-2 border-ink2/30 px-3 py-1 hover:border-marigold hover:text-marigold transition">{s}</button>
            ))}</div>
          </div>
        ) : messages.map((m, i) => (
          <div key={i} data-testid={`mendel-ai-msg-${m.role}`} className={m.role === "user" ? "text-right" : ""}>
            <div className={`inline-block max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-marigold text-cream" : "bg-ink/5 text-ink"}`}>{m.content}</div>
            {m.role === "assistant" && project && (
              <button onClick={() => saveToNotebook(m.content)} className="block text-[10px] text-ink2 hover:text-marigold underline mt-1">Save to notebook</button>
            )}
          </div>
        ))}
        {busy && <div className="text-ink2 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> Mendel is thinking…</div>}
      </div>

      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Mendel AI…" className="flex-1 px-3 py-2 rounded-full border-2 border-ink bg-cream text-sm" data-testid="mendel-ai-input" />
        <button onClick={send} disabled={busy} className="btn-primary text-sm" data-testid="mendel-ai-send"><MessageSquare className="w-3.5 h-3.5" /> Send</button>
      </div>
    </div>
  );
}

// ─────────── Notebook pane ───────────
function NotebookPane({ project, onRefreshProject }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/mendel/projects/${project.id}/notebook`, { kind: "note", note: note.trim() });
      setNote(""); onRefreshProject?.();
    } catch { toast.error("Could not save note"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="mendel-notebook-pane">
      <h2 className="font-heading text-3xl mb-1">{project.title}</h2>
      {project.abstract && <p className="text-ink2 mb-6">{project.abstract}</p>}

      <div className="card-soft p-4 mb-4">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a research note, observation, or hypothesis…" className="w-full px-3 py-2 rounded-md border-2 border-ink bg-cream text-sm resize-none" data-testid="notebook-input" />
        <div className="mt-2 text-right">
          <button onClick={addNote} disabled={busy || !note.trim()} className="btn-primary text-xs" data-testid="notebook-add">Add entry</button>
        </div>
      </div>

      <NotebookImportPanel project={project} onRefreshProject={onRefreshProject} />

      <ul className="space-y-2" data-testid="notebook-entries">
        {(project.notebook || []).slice().reverse().map((e) => (
          <li key={e.id} className="card-soft p-4">
            <div className="text-[10px] uppercase tracking-widest font-mono text-ink2 mb-1">
              {e.kind} {e.target && <span className="text-marigold">· {e.target}</span>} · {new Date(e.added_at).toLocaleString()}
            </div>
            <div className="text-sm whitespace-pre-wrap">{e.note}</div>
          </li>
        ))}
        {(!project.notebook || project.notebook.length === 0) && (
          <li className="text-ink2 text-sm">No notes yet — start by exploring a gene or running a simulation.</li>
        )}
      </ul>
    </div>
  );
}

// ─────────── Notebook Import (FASTA/VCF row preview) ───────────
function NotebookImportPanel({ project, onRefreshProject }) {
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setJob(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/mendel/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob(data);
      // Poll until parsed
      const poll = async (id, tries = 0) => {
        if (tries > 25) return;   // ~25s ceiling
        try {
          const { data: fresh } = await api.get(`/mendel/jobs/${id}`);
          setJob(fresh);
          if (fresh.status !== "parsed" && fresh.status !== "failed") {
            setTimeout(() => poll(id, tries + 1), 1000);
          }
        } catch { /* stop */ }
      };
      poll(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const saveToNotebook = async () => {
    if (!job) return;
    const note = `Imported ${job.filename} (${job.detected_format}) — ${job.row_count} rows${job.truncated ? " (truncated)" : ""}.`;
    try {
      await api.post(`/mendel/projects/${project.id}/notebook`, { kind: "note", target: job.filename, note });
      toast.success("Import summary saved to notebook");
      onRefreshProject?.();
    } catch { toast.error("Could not save"); }
  };

  return (
    <div className="card-soft p-4 mb-4" data-testid="notebook-import-panel">
      <div className="label-eyebrow mb-2">📥 Import FASTA / VCF / CSV</div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="file"
          accept=".fasta,.fa,.fna,.fastq,.fq,.vcf,.csv,.tsv,.bed,.gff,.gtf,.txt,.json"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-xs"
          data-testid="notebook-import-file"
        />
        <button onClick={upload} disabled={busy || !file} className="btn-primary text-xs" data-testid="notebook-import-upload">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ArrowRight className="w-3 h-3" /> Upload &amp; parse</>}
        </button>
        {job && <div className="text-[10px] font-mono text-ink2" data-testid="notebook-import-status">
          status: <span className="text-marigold">{job.status}</span>
          {job.detected_format ? <> · {job.detected_format}</> : null}
          {typeof job.row_count === "number" ? <> · {job.row_count} rows{job.truncated ? " (capped)" : ""}</> : null}
        </div>}
      </div>

      {job?.error && (
        <div className="text-xs text-[#C1666B] bg-[#C1666B]/10 border border-[#C1666B]/30 rounded-md px-3 py-2 mb-2" data-testid="notebook-import-error">
          Parse failed: {job.error}
        </div>
      )}

      {job?.status === "parsed" && Array.isArray(job.preview_rows) && job.preview_rows.length > 0 && (
        <div data-testid="notebook-import-preview">
          <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mb-2">First {job.preview_rows.length} rows</div>
          <ImportPreviewTable rows={job.preview_rows} format={job.detected_format} />
          <div className="mt-3">
            <button onClick={saveToNotebook} className="btn-outline-ink text-xs" data-testid="notebook-import-save">
              <Plus className="w-3 h-3" /> Save import summary to notebook
            </button>
          </div>
        </div>
      )}
      {job?.status === "parsed" && (!job.preview_rows || job.preview_rows.length === 0) && (
        <div className="text-xs text-ink2 italic" data-testid="notebook-import-empty">Parsed successfully — no rows detected in this file.</div>
      )}
    </div>
  );
}

function ImportPreviewTable({ rows, format }) {
  // Column order per format so genomics readers see the familiar layout
  const preferred = format === "VCF"
    ? ["chrom", "pos", "id", "ref", "alt", "qual", "filter", "info"]
    : format === "FASTA"
      ? ["header", "length", "sequence_preview"]
      : format === "FASTQ"
        ? ["header", "length", "sequence_preview", "quality_preview"]
        : null;

  // Collect all keys across rows; put preferred first, then any extras
  const allKeys = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
  const cols = preferred
    ? [...preferred.filter((k) => allKeys.includes(k)), ...allKeys.filter((k) => !preferred.includes(k))]
    : allKeys;

  return (
    <div className="overflow-x-auto border-2 border-ink rounded-md" data-testid="import-preview-table">
      <table className="w-full text-xs font-mono">
        <thead className="bg-ink/5">
          <tr>
            <th className="px-2 py-1.5 text-left border-b border-ink/20 text-ink2">#</th>
            {cols.map((c) => (
              <th key={c} className="px-2 py-1.5 text-left border-b border-ink/20 uppercase tracking-widest text-[10px] text-ink2 font-bold">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-cream" : "bg-paper"} data-testid={`import-preview-row-${i}`}>
              <td className="px-2 py-1 text-ink2">{i + 1}</td>
              {cols.map((c) => (
                <td key={c} className="px-2 py-1 max-w-[240px] truncate" title={String(r[c] ?? "")}>{String(r[c] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
